import _ from 'lodash';
import React from 'react';

import Table from 'components/Table';
import Money from 'components/Money';
import Button from 'components/Button';
import Loading from 'components/Loading';
import CheckBox from 'components/CheckBox';
import ThreeStateCheckBox from 'components/ThreeStateCheckBox';
import InvoiceStatusTag from 'components/InvoiceStatusTag';
import ClickIconWithNoPropagation from 'components/ClickIconWithNoPropagation';
import {YearSplitter} from './components';
import {
  YEAR_SPLITTER,
  getDataWithYearSplitters
} from 'containers/BookingSetDetail/utils';

import {SCHEDULE_TYPES_HUMAN_VALUES, SCHEDULE_ITEM_TYPES} from 'ducks/tenancy';

import {splitListBy} from 'utils/common';

import {formatDate} from 'utils/datetime';

import './styles.css';

const InvoiceRow = ({
  invoice,
  redirectToBookingDetail,
  openInvoiceDetailModal,
  selectedInvoicesUuids,
  selectInvoice
}) => (
  <Table.Row
    onClick={() => {
      openInvoiceDetailModal(
        SCHEDULE_ITEM_TYPES.INVOICE,
        invoice.id,
        invoice.tenancyId
      );
    }}>
    <Table.Cell className="checkbox">
      <ClickIconWithNoPropagation>
        <CheckBox
          name={`invoice${invoice.uuid}`}
          label=""
          value={_.includes(selectedInvoicesUuids, invoice.uuid)}
          onChange={() => selectInvoice(invoice.uuid)}
        />
      </ClickIconWithNoPropagation>
    </Table.Cell>
    <Table.Cell className="booking">
      <ClickIconWithNoPropagation
        onClick={() => redirectToBookingDetail({bookingId: invoice.bookingId})}>
        <div className="colab-bold">#{invoice.bookingId}</div>
      </ClickIconWithNoPropagation>
    </Table.Cell>
    <Table.Cell className="date">{formatDate(invoice.date)}</Table.Cell>
    <Table.Cell className="dueDate">{formatDate(invoice.dueDate)}</Table.Cell>
    <Table.Cell className="description">{invoice.description}</Table.Cell>
    <Table.Cell className="category">
      {_.get(SCHEDULE_TYPES_HUMAN_VALUES, invoice.scheduleType)}
    </Table.Cell>
    <Table.Cell className="status">
      <InvoiceStatusTag overdue={invoice.overdue} status={invoice.status} />
    </Table.Cell>
    <Table.Cell className="due">
      <Money amount={invoice.totalIncludingTax} balance />
    </Table.Cell>
  </Table.Row>
);

const InvoiceTableRow = ({
  index,
  element,
  redirectToBookingDetail,
  openInvoiceDetailModal,
  selectedInvoicesUuids,
  selectInvoice
}) => {
  switch (element.type) {
    case YEAR_SPLITTER:
      return <YearSplitter key={index} year={element.year} />;
    default:
      return (
        <InvoiceRow
          key={index}
          invoice={element}
          redirectToBookingDetail={redirectToBookingDetail}
          openInvoiceDetailModal={openInvoiceDetailModal}
          selectedInvoicesUuids={selectedInvoicesUuids}
          selectInvoice={selectInvoice}
        />
      );
  }
};

class InvoicesTable extends React.Component<any> {
  state = {
    selectedInvoicesUuids: []
  };

  selectAllInvoices = () => {
    const {invoices} = this.props;
    this.setState({selectedInvoicesUuids: invoices.map(inv => inv.uuid)});
  };

  deselectAllInvoices = () => {
    this.setState({selectedInvoicesUuids: []});
  };

  selectInvoice = invoiceUuid => {
    this.setState({
      selectedInvoicesUuids: _.xor(this.state.selectedInvoicesUuids, [
        invoiceUuid
      ])
    });
  };

  voidInvoices = () => {
    const invoicesIdsToVoid = this.props.invoices
      .filter(invoice =>
        _.includes(this.state.selectedInvoicesUuids, invoice.uuid)
      )
      .map(invoice => invoice.id);

    this.props.voidInvoices({invoicesIds: invoicesIdsToVoid});
  };

  render() {
    const {
      invoices,
      isLoading,
      redirectToBookingDetail,
      openInvoiceDetailModal,
      voidingInvoices
    } = this.props;

    const {selectedInvoicesUuids} = this.state;
    const dataWithSplitters = getDataWithYearSplitters(invoices);

    const [selected, deselected] = splitListBy(invoices, invoice =>
      _.includes(selectedInvoicesUuids, invoice.uuid)
    );

    return (
      <div className="InvoicesTable">
        <div>
          <Table>
            <Table.Header>
              <Table.HeaderItem className="checkbox">
                <ClickIconWithNoPropagation>
                  <ThreeStateCheckBox
                    selected={selected.length > 0}
                    deselected={deselected.length > 0}
                    onSelectAll={this.selectAllInvoices}
                    onDeselectAll={this.deselectAllInvoices}
                  />
                </ClickIconWithNoPropagation>
              </Table.HeaderItem>
              <Table.HeaderItem heading="Booking" className="booking" />
              <Table.HeaderItem heading="Date" className="date" />
              <Table.HeaderItem heading="Due Date" className="dueDate" />
              <Table.HeaderItem heading="Description" className="description" />
              <Table.HeaderItem heading="Category" className="category" />
              <Table.HeaderItem heading="Status" className="status" />
              <Table.HeaderItem heading="Due" className="due" />
            </Table.Header>
          </Table>
          <div className="InvoicesTable__body">
            {_.isEmpty(dataWithSplitters) && isLoading && <Loading />}
            <Table>
              <Table.Body>
                {dataWithSplitters.map((element, index) => (
                  <InvoiceTableRow
                    key={index}
                    index={index}
                    element={element}
                    redirectToBookingDetail={redirectToBookingDetail}
                    openInvoiceDetailModal={openInvoiceDetailModal}
                    selectedInvoicesUuids={selectedInvoicesUuids}
                    selectInvoice={this.selectInvoice}
                  />
                ))}
              </Table.Body>
            </Table>
          </div>
        </div>
        <div className="actions colab-row-space-between">
          <Button white onClick={this.props.closeModal}>
            Cancel
          </Button>
          <Button
            blue={!voidingInvoices}
            onClick={this.voidInvoices}
            isLoading={voidingInvoices}
            disabled={voidingInvoices || selectedInvoicesUuids.length == 0}>
            Void
          </Button>
        </div>
      </div>
    );
  }
}

export default InvoicesTable;
